import type React from 'react'
import {useMemo, useState} from 'react'
import {useParams} from 'react-router-dom'
import {Copy, Check, ChevronDown, ChevronRight} from 'lucide-react'
import {useI18n} from '@/i18n'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {Stack} from '@/components/shared/Stack'
import {CodeText} from '@/components/shared/CodeText'
import {MonoText} from '@/components/shared/MonoText'
import {SmallText} from '@/components/shared/SmallText'
import {Strong} from '@/components/shared/Strong'
import {useToast} from '@/hooks/use-toast'
import {useCopyToClipboard} from '@/hooks/use-copy-to-clipboard'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Pagination} from '@/components/ui/pagination'
import {Skeleton} from '@/components/ui/skeleton'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {MutedText} from '@/components/shared/MutedText'
import {DetailField} from '@/components/shared/DetailField'
import {TextLink} from '@/components/shared/TextLink'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {PageLayout} from '@/components/shared/PageLayout'
import {DetailList} from '@/components/shared/DetailList'
import {IconText} from '@/components/shared/IconText'
import {StatusText} from '@/components/shared/StatusText'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {ErrorLogControllerOccurrencesByTemplateEndpoint} from '@/types/endpoint-contracts'
import {useErrorLogDetail, useErrorLogsByTemplate} from '@/hooks/use-error-logs'
import {formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import type {ErrorLog, LineDiffInfo} from '@/types/background-jobs'
import {TruncatedCell} from '@/components/shared/TruncatedCell'

export function ErrorLogDetail() {
    const {id} = useParams<{id: string}>()
    const i18n = useI18n()
    const {toast} = useToast()
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
    } = useServerPagination({
        endpoint: ErrorLogControllerOccurrencesByTemplateEndpoint,
        defaultSortBy: ErrorLogControllerOccurrencesByTemplateEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
        sortable: false,
    })

    const {data: record, isLoading, error} = useErrorLogDetail(id)

    const showOccurrencesTable = record?.isTemplate && record.occurrenceCount > 1
    const {data: occurrences, isLoading: occLoading} = useErrorLogsByTemplate(
        showOccurrencesTable ? id : undefined,
        queryString,
    )

    const {copy: copyTrace, copied: traceCopied} = useCopyToClipboard()

    const handleCopyTrace = () => {
        if (record?.stackTrace) {
            void copyTrace(record.stackTrace).then(() => {
                toast({description: i18n.errorLogs.patternDetail.copiedTrace as string})
            })
        }
    }

    return (
        <PageLayout
            data-testid="error-log-detail-page"
            variant="detail"
            title={record?.exceptionType ?? i18n.errorLogs.patternDetail.title}
            description={record ? `${record.sourceModule} — ${formatTimestamp(record.createdAt)}` : undefined}
            icon={CONCEPT_ICON.errorLog}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(record, 'ERROR_LOG') : undefined)}
            backTo={routes.admin.errorLogs.list()}
        >
            {record && <>
            <div data-testid="pattern-detail-page">
            {/* Error Info Card */}
            <DetailsCard
                data-testid="pattern-info"
                title={i18n.errorLogs.patternDetail.patternInfo}
                fields={[
                    {label: i18n.errorLogs.occurrenceDetail.message, value: <SmallText className="break-words">{record.message}</SmallText>, fullWidth: true},
                    {label: i18n.errorLogs.columns.sourceType, value: record.sourceType},
                    {
                        label: i18n.errorLogs.columns.sourceEntity,
                        value: record.sourceId
                            ? <Badge variant="outline" className="font-mono text-xs">{record.sourceId.substring(0, 8)}</Badge>
                            : undefined,
                    },
                    ...(record.appErrorCode ? [{label: i18n.errorLogs.patternDetail.errorCode, value: <Badge variant="outline">{record.appErrorCode}</Badge>}] : []),
                    {
                        label: i18n.errorLogs.patternDetail.totalOccurrences,
                        value: <Badge variant={record.occurrenceCount > 100 ? 'destructive' : record.occurrenceCount > 10 ? 'warning' : 'outline'}>{record.occurrenceCount.toLocaleString()}</Badge>,
                    },
                    ...(record.lastSeenAt ? [{label: i18n.errorLogs.patternDetail.lastSeen, value: formatRelativeTime(record.lastSeenAt)}] : []),
                ]}
            />

            {/* Context Card */}
            {record.context && Object.keys(record.context).length > 0 && (
                <Card data-testid="occurrence-context">
                    <CardHeader>
                        <CardTitle data-testid="card-title-occurrence-context">{i18n.errorLogs.occurrenceDetail.context}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <DetailList columns={1} className="gap-2">
                            {Object.entries(record.context).map(([key, value]) => (
                                <DetailField
                                    key={key}
                                    label={key}
                                    value={
                                        typeof value === 'object' ? (
                                            <CodeBlock>{JSON.stringify(value, null, 2)}</CodeBlock>
                                        ) : (
                                            <MonoText>{String(value)}</MonoText>
                                        )
                                    }
                                />
                            ))}
                        </DetailList>
                    </CardContent>
                </Card>
            )}

            {/* Request Context Card */}
            <RequestContextCard record={record}/>

            {/* Stack Trace Card */}
            {record.stackTrace ? (
                <Card data-testid="pattern-stacktrace">
                    <CardHeader className="pb-3">
                        <InlineRow justify="between">
                            <CardTitle data-testid="card-title-stack-trace">{i18n.errorLogs.occurrenceDetail.stackTrace}</CardTitle>
                            <Button variant="ghost" size="sm" onClick={handleCopyTrace} data-testid="btn-copy-trace">
                                {traceCopied
                                    ? <Icon icon={Check} size="xs" className="mr-1"/>
                                    : <Icon icon={Copy} size="xs" className="mr-1"/>}
                                {i18n.errorLogs.patternDetail.copyTrace}
                            </Button>
                        </InlineRow>
                    </CardHeader>
                    <CardContent>
                        <StackTraceDisplay
                            stackTrace={record.stackTrace}
                            lineDiffs={record.lineDiffs}
                        />
                        {record.lineDiffs && record.lineDiffs.length > 0 && (
                            <MutedText size="xs" className="block mt-2">
                                {i18n.errorLogs.occurrenceDetail.diffNote}
                            </MutedText>
                        )}
                        {(!record.lineDiffs || record.lineDiffs.length === 0) && record.templateRecordId && (
                            <MutedText size="xs" className="block mt-2">
                                {i18n.errorLogs.occurrenceDetail.identicalToTemplate}
                            </MutedText>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent className="py-6">
                        <MutedText>{i18n.errorLogs.occurrenceDetail.noStackTrace}</MutedText>
                    </CardContent>
                </Card>
            )}

            {/* Occurrences Table — only for templates with multiple occurrences */}
            {showOccurrencesTable && (
                <Card data-testid="occurrences-card">
                    <CardHeader>
                        <CardTitle data-testid="card-title-occurrences" className="text-lg">
                            {i18n.errorLogs.patternDetail.occurrencesTitle}
                            {occurrences && (
                                <MutedText size="sm" className="ml-2 font-normal">
                                    {i18n.errorLogs.patternDetail.occurrencesCount({count: occurrences.totalElements})}
                                </MutedText>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table data-testid="occurrences-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-time" className="w-36">{i18n.errorLogs.columns.time}</TableHead>
                                    <TableHead data-testid="col-source-entity">{i18n.errorLogs.columns.sourceEntity}</TableHead>
                                    <TableHead data-testid="col-message">{i18n.errorLogs.columns.message}</TableHead>
                                    <TableHead data-testid="col-context" className="w-20">{i18n.errorLogs.columns.context}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {occLoading ? (
                                    Array.from({length: 5}).map((_, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell><Skeleton className="h-4 w-24"/></TableCell>
                                            <TableCell><Skeleton className="h-4 w-32"/></TableCell>
                                            <TableCell><Skeleton className="h-4 w-48"/></TableCell>
                                            <TableCell><Skeleton className="h-4 w-8"/></TableCell>
                                        </TableRow>
                                    ))
                                ) : occurrences?.content.length === 0 ? (
                                    <TableEmptyState
                                        colSpan={4}
                                        hasFilters={false}
                                        matchMessage={i18n.errorLogs.noOccurrences as string}
                                        emptyMessage={i18n.errorLogs.noOccurrences as string}
                                    />
                                ) : (
                                    occurrences?.content.map((occ) => (
                                        <ClickableTableRow
                                            key={occ.errorLogId}
                                            to={routes.admin.errorLogs.detail(occ.errorLogId)}
                                            data-testid={`occurrence-row-${occ.errorLogId}`}
                                        >
                                            <TableCell title={formatTimestamp(occ.createdAt)}>
                                                <MutedText>{formatRelativeTime(occ.createdAt)}</MutedText>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {occ.sourceType}
                                                {occ.sourceId && (
                                                    <MutedText size="xs" className="ml-1 font-mono">
                                                        {occ.sourceId.substring(0, 8)}
                                                    </MutedText>
                                                )}
                                            </TableCell>
                                            <TruncatedCell className="text-sm" title={occ.message}>
                                                {occ.message}
                                            </TruncatedCell>
                                            <TableCell>
                                                {occ.context && Object.keys(occ.context).length > 0 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        {Object.keys(occ.context).length}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                        </ClickableTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>

                        {occurrences && occurrences.totalElements > 0 && (
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalItems={occurrences.totalElements}
                                totalPages={occurrences.totalPages}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </CardContent>
                </Card>
            )}
            </div>
        </>}
        </PageLayout>
    )
}

function RequestContextCard({record}: {record: ErrorLog}) {
    const i18n = useI18n()
    const {toast} = useToast()
    const {hasPermission} = useAuth()
    const [headersExpanded, setHeadersExpanded] = useState(false)
    const {copy: copyCorrelationId, copied: correlationIdCopied} = useCopyToClipboard()

    const hasHeaders = record.requestHeaders && Object.keys(record.requestHeaders).length > 0
    const hasAnyField = !!(record.requestMethod || record.requestUrl || record.userAgent
        || record.requestCorrelationId || hasHeaders || record.authUsername
        || record.sessionId || record.actionId || record.e2eRunId)

    if (!hasAnyField) return null

    const handleCopyCorrelationId = () => {
        if (record.requestCorrelationId) {
            void copyCorrelationId(record.requestCorrelationId).then(() => {
                toast({description: i18n.errorLogs.requestContext.copiedCorrelationId as string})
            })
        }
    }

    return (
        <Card data-testid="request-context-card">
            <CardHeader>
                <InlineRow justify="between">
                    <CardTitle data-testid="card-title-request-context">{i18n.errorLogs.requestContext.title}</CardTitle>
                    <Badge variant={record.requestCorrelationId ? 'default' : 'secondary'} data-testid="request-source-badge">
                        {record.requestCorrelationId
                            ? i18n.errorLogs.requestContext.appRequest
                            : i18n.errorLogs.requestContext.externalRequest}
                    </Badge>
                </InlineRow>
            </CardHeader>
            <CardContent>
                <DetailList columns={1}>
                    {(record.requestMethod || record.requestUrl) && (
                        <DetailField
                            label={String(i18n.errorLogs.requestContext.method)}
                            value={
                                <MonoText>
                                    {record.requestMethod && (
                                        <Strong className="mr-2">{record.requestMethod}</Strong>
                                    )}
                                    {record.requestUrl && (
                                        <SmallText className="break-all">{record.requestUrl}</SmallText>
                                    )}
                                </MonoText>
                            }
                        />
                    )}
                    {record.userAgent && (
                        <DetailField
                            label={String(i18n.errorLogs.requestContext.userAgent)}
                            value={
                                <SmallText className="truncate block" data-testid="user-agent-value" title={record.userAgent}>
                                    {record.userAgent}
                                </SmallText>
                            }
                        />
                    )}
                    {record.requestCorrelationId && (
                        <DetailField
                            label={String(i18n.errorLogs.requestContext.correlationId)}
                            value={
                                <InlineRow gap="md" className="mt-1">
                                    <CodeText>{record.requestCorrelationId}</CodeText>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={handleCopyCorrelationId}
                                        data-testid="btn-copy-correlation-id"
                                    >
                                        {correlationIdCopied
                                            ? <Icon icon={Check} size="xs"/>
                                            : <Icon icon={Copy} size="xs"/>}
                                    </Button>
                                </InlineRow>
                            }
                        />
                    )}
                    {record.authUsername && (
                        <DetailField
                            label={String(i18n.errorLogs.requestContext.authUser)}
                            value={
                                <SmallText data-testid="auth-username-value">
                                    {record.authUsername}
                                </SmallText>
                            }
                        />
                    )}
                    {hasHeaders && (
                        <DetailField
                            label={
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    type="button"
                                    className="h-auto p-0"
                                    onClick={() => setHeadersExpanded(prev => !prev)}
                                    data-testid="btn-toggle-headers"
                                >
                                    <IconText icon={headersExpanded ? ChevronDown : ChevronRight} size="xs">
                                        {i18n.errorLogs.requestContext.headers}
                                    </IconText>
                                </Button>
                            }
                            value={headersExpanded ? (
                                <div data-testid="headers-content">
                                    <CodeBlock>
                                        {JSON.stringify(record.requestHeaders, null, 2)}
                                    </CodeBlock>
                                </div>
                            ) : null}
                        />
                    )}
                    {record.sessionId && (
                        <DetailField
                            label={String(i18n.errorLogs.detail.sessionId)}
                            value={
                                <InlineRow gap="md" data-testid="session-id-value">
                                    <CodeText>{record.sessionId}</CodeText>
                                    {hasPermission(permissions.observability.view) && (
                                        <TextLink
                                            to={routes.admin.requestInspector.detail(record.sessionId)}
                                            testId="session-id-link"
                                            size="xs"
                                        >
                                            {i18n.errorLogs.detail.sessionLink}
                                        </TextLink>
                                    )}
                                </InlineRow>
                            }
                        />
                    )}
                    {!record.sessionId && (
                        <DetailField
                            label={String(i18n.errorLogs.detail.sessionId)}
                            value={undefined}
                        />
                    )}
                    {record.actionId && (
                        <DetailField
                            label={String(i18n.errorLogs.detail.actionId)}
                            value={<span data-testid="action-id-value"><CodeText>{record.actionId}</CodeText></span>}
                        />
                    )}
                    {record.e2eRunId && (
                        <DetailField
                            label={String(i18n.errorLogs.detail.e2eRunId)}
                            value={<span data-testid="e2e-run-id-value"><MonoText>{record.e2eRunId}</MonoText></span>}
                        />
                    )}
                    {record.e2eTestClass && (
                        <DetailField
                            label={String(i18n.errorLogs.detail.e2eTestClass)}
                            value={<span data-testid="e2e-test-class-value"><MonoText>{record.e2eTestClass}</MonoText></span>}
                        />
                    )}
                    {record.e2eTestMethod && (
                        <DetailField
                            label={String(i18n.errorLogs.detail.e2eTestMethod)}
                            value={<span data-testid="e2e-test-method-value"><MonoText>{record.e2eTestMethod}</MonoText></span>}
                        />
                    )}
                </DetailList>
            </CardContent>
        </Card>
    )
}

function renderLineWithHighlights(line: string, lineDiff: LineDiffInfo): React.ReactNode[] {
    const parts: React.ReactNode[] = []
    let cursor = 0

    for (const change of lineDiff.changes) {
        if (change.start > cursor) {
            parts.push(<span key={`t-${cursor}`}>{line.substring(cursor, change.start)}</span>)
        }
        parts.push(
            <StatusText key={`c-${change.start}`} tone="danger" highlight>
                {change.actualText}
            </StatusText>
        )
        cursor = change.start + change.actualText.length
    }

    if (cursor < line.length) {
        parts.push(<span key={`e-${cursor}`}>{line.substring(cursor)}</span>)
    }

    return parts
}

function StackTraceDisplay({stackTrace, lineDiffs}: {stackTrace: string, lineDiffs: LineDiffInfo[] | null}) {
    const lines = useMemo(() => stackTrace.split('\n'), [stackTrace])
    const diffsByLine = useMemo(() => {
        const map = new Map<number, LineDiffInfo>()
        if (lineDiffs) {
            for (const ld of lineDiffs) {
                map.set(ld.lineNumber, ld)
            }
        }
        return map
    }, [lineDiffs])

    return (
        <CodeBlock maxHeight="xl" preserveLines data-testid="stacktrace-pre">
            {lines.map((line, idx) => {
                const lineDiff = diffsByLine.get(idx)
                if (lineDiff) {
                    return (
                        <Stack key={idx} className="bg-status-danger-subtle dark:bg-status-danger-subtle/20">
                            {renderLineWithHighlights(line, lineDiff)}
                        </Stack>
                    )
                }
                return (
                    <div key={idx}>
                        {line}
                    </div>
                )
            })}
        </CodeBlock>
    )
}
